import {
    EventrDate,
    IField,
    IValidation,
    ValidationTypes,
} from '@eventr/common'
import { DatePicker, useValidatorContext } from '@eventr/common-storybook'
import { useEffect, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import i18n from '../../../i18n/i18next'
import { useBoundStore } from '../../../state/bound.store'
import { useLanguageContext } from '../../../state/languageState/languageContext'
import { useEditSectionContext } from '../editSectionProvider'
interface IDateEditFieldProps {
    field: IField
    _id?: string
}

export const DateEditField = ({ field, _id }: IDateEditFieldProps) => {
    const { updateValues, values, getValue, deleteSetValue } =
        useEditSectionContext()
    const { isFieldValid } = useValidatorContext()
    const { getEventInfoV2 } = useBoundStore(
        useShallow((s) => {
            return {
                getEventInfoV2: s.getEventInfoV2,
            }
        })
    )
    const { getLanguageISO639Code } = useLanguageContext()

    const [state, setState] = useState<string | undefined>(undefined)
    const [isError, setIsError] = useState<boolean>(false)
    const [textError, setTextError] = useState<string>('')

    useEffect(() => {
        const initFieldValue = getValue(field, _id)

        if (
            typeof initFieldValue !== 'string' &&
            initFieldValue !== null &&
            typeof initFieldValue !== 'number'
        ) {
            setState(JSON.stringify(initFieldValue))
        } else {
            setState(initFieldValue ?? undefined)
        }
    }, [field, _id, getValue])

    /// Validate onchange.
    useEffect(() => {
        try {
            const valid = isFieldValid(field, _id, state, values)
            if ((valid !== true) !== isError) {
                setIsError(valid !== true)
            }
            if (valid !== true) {
                setTextError(valid as string)
            } else {
                setTextError('')
            }
        } catch {
            /// idk why it fails sometimes.
        }
    }, [state, field, _id, isFieldValid, setIsError, isError, setTextError])

    /// Set time of the datetime for dates. start or end of day.
    /// Uses validation to decide if its start or end
    function setTimeOfDateField(dateToChange: EventrDate) {
        if (field.field_type === 'date') {
            if (
                field.type_properties?.validations?.find((validation) => {
                    return validation.type === ValidationTypes.beforeDate
                })
            ) {
                dateToChange.setFromObject({
                    hour: 0,
                    minute: 0,
                    second: 1,
                    millisecond: 0,
                })
                return
            }
            if (
                field.type_properties?.validations?.find((validation) => {
                    return validation.type === ValidationTypes.afterDate
                })
            ) {
                dateToChange.setFromObject({
                    hour: 23,
                    minute: 59,
                    second: 59,
                    millisecond: 999,
                })
                return
            }
        }
        return
    }

    const isRequired = useMemo(() => {
        return field.type_properties?.validations?.some(
            (v) => v.type === ValidationTypes.required
        )
    }, [field.type_properties?.validations])

    /// To limit the selection of the field.
    /// undifined will mean no limit.
    function getMaxMinDateIfExists(
        validationType: ValidationTypes
    ): EventrDate | undefined {
        try {
            var dateVaidationObject: IValidation =
                field.type_properties.validations?.find((validationObject) => {
                    if (validationObject.type === validationType) {
                        return true
                    } else {
                        return false
                    }
                })

            if (dateVaidationObject) {
                const maxMinField = useBoundStore
                    .getState()
                    .getFieldById(dateVaidationObject.fieldValue)
                if (
                    values &&
                    maxMinField &&
                    values[maxMinField.type_properties.json_key]
                ) {
                    /// Works great IF is under the top level parent. ie values.
                    /// However, may be further down tree... idk how to deal with that...
                    const rangeDate = new EventrDate(
                        values[maxMinField.type_properties.json_key]
                    )
                    return rangeDate
                } else {
                    return undefined
                }
            } else {
                return undefined
            }
        } catch {
            return undefined
        }
    }

    function onSave(date: string | undefined) {
        if (date === undefined) {
            /// Deal with onclear func
            setState(date)
            deleteSetValue({
                value: date,
                field: field,
                _id: _id,
            })
            return
        } else {
            const newDate = new EventrDate(date)
            setTimeOfDateField(newDate)
            setState(newDate.dateTimeString)
            updateValues({
                value: newDate.dateTimeString,
                field: field,
                _id: _id,
            })
        }
    }

    return (
        <DatePicker
            id={field.field_name}
            date={state}
            drawerHeader={i18n.t(`edit-itinerary.fields.select-date`)}
            required={isRequired}
            outOfRangePopup
            isError={isError}
            textError={textError}
            onSave={onSave}
            fieldLabel={i18n.t(`edit-itinerary.fields.${field.id}`)}
            initialState={
                getMaxMinDateIfExists(
                    ValidationTypes.afterDate
                )?.toItinLocalDateString() ?? getEventInfoV2()?.data.start_date
            }
            minDate={
                getMaxMinDateIfExists(ValidationTypes.afterDate) ??
                (field.type_properties.ignoreItinDate
                    ? undefined
                    : new EventrDate(getEventInfoV2()?.data.start_date))
            }
            maxDate={
                getMaxMinDateIfExists(ValidationTypes.beforeDate) ??
                (field.type_properties.ignoreItinDate
                    ? undefined
                    : new EventrDate(getEventInfoV2()?.data.end_date))
            }
            extendMinMaxByDays={1}
            selectText={i18n.t('edit-itinerary.fields.select-date')}
            saveText={i18n.t('general.done')}
            clearText={i18n.t('general.clear')}
            locale={getLanguageISO639Code()}
            highlightRangeStart={getEventInfoV2().data.start_date}
            highlightRangeEnd={getEventInfoV2().data.end_date}
        />
    )
}
